/**
 * **Who the signed-in person is** — Esoft/Efkt staff rather than a customer. Gates employee-only UI
 * (adding order lines, editing a user's access, the copy-material and virtual-staging controls).
 *
 * Derived from the email domain, because no API returns it any more.
 */
export const IS_ESOFT_USER_KEY = 'esoft.user';
/**
 * **How this session signed in** — through the employee AD journey (`esoftAuth`, `/esoft`) rather
 * than the customer B2C one (`b2cAuth`, `/login`). Decides *routing*: which login page a logout
 * lands on, and whether the session needs the pre-Efkt `/active-directory` picker before it can
 * resolve a customer at all.
 *
 * **These two used to be one key, and that was the bug.** `IS_ESOFT_USER_KEY` was written purely
 * from which sign-in call was made, so "is staff" and "came in through AD" could not disagree. They
 * do now: the shared test accounts are staff addresses (`@efkt.com`) belonging to *migrated
 * customers*, who sign in through the ordinary `/login` form. Answering "where do I land?" with the
 * staff flag bounced exactly those accounts back to `/login` in a loop. Keep them separate.
 */
export const IS_AD_SESSION_KEY = 'auth.ad.session';
/**
 * The trailing dot is load-bearing: the TLD varies (`esoft.com`, `esoft.dk`, `efkt.net`), so the
 * domain cannot be listed exhaustively, and matching `@esoft.` rather than `@esoft` is what stops
 * `someone@esoftware.io` from counting. Deliberately literal — `@mail.esoft.com` does not match.
 */
export function isEsoftEmail(email) {
    return /@(esoft|efkt)\./i.test(email || '');
}
/** Whether this session came in through the employee AD journey. See `IS_AD_SESSION_KEY`. */
export function isAdSession() {
    return typeof window !== 'undefined' && localStorage.getItem(IS_AD_SESSION_KEY) === 'true';
}
export const HEADER_USER_ID_KEY = 'header.user.id';
export const AUTH_LOGIN_KEY = 'auth.login';
export const AUTH_LOGOUT_KEY = 'auth.logout';
export const AUTH_SCHEMA_KEY = 'auth.schema';
export const AUTH_REFRESHING = 'auth.refreshing';
export const RESET_PASSWORD_CODE_KEY = 'password.error.code';
export const AUTH_RESET_PASSWORD_KEY = 'auth.reset.password';
export const AUTH_REDIRECT_URL = 'auth.redirect.url';
export const MINDWORKING_REDIRECT_REFERENCE = 'mindworking.reference';
/**
 * Stores what login-URL discovery returned, whichever backend answered.
 *
 * Everything downstream — the refresh iframe, the `Authorization` scheme word the datahub client
 * prefixes, the logout URL, the reset-password jump on `/auth-redirect` — reads these keys and not
 * the response, so a session started against Efkt and one started against datahub are
 * indistinguishable from that point on. That is the whole point of the Efkt endpoints answering in
 * retail-core's shape.
 *
 * `resetPasswordUrl` is written even when absent (Efkt omits it when no
 * `AzureAdB2C:PasswordResetPolicyId` is configured) because `localStorage.setItem` with `undefined`
 * stores the string `"undefined"` in the pre-Efkt code this replaces, and `/auth-redirect` only
 * reads it behind a truthiness check either way — keeping the write unconditional preserves the
 * existing behaviour exactly rather than quietly changing it.
 */
export function persistLoginSettings(settings, { email, viaAdJourney }) {
    // An AD sign-in is staff by definition and carries no email to inspect; a B2C sign-in is staff
    // only if the address says so. See the two key docs above for why this is not one flag.
    localStorage.setItem(IS_ESOFT_USER_KEY, String(viaAdJourney || isEsoftEmail(email)));
    localStorage.setItem(IS_AD_SESSION_KEY, String(viaAdJourney));
    localStorage.setItem(AUTH_SCHEMA_KEY, settings === null || settings === void 0 ? void 0 : settings.authenticationScheme);
    localStorage.setItem(AUTH_LOGIN_KEY, settings === null || settings === void 0 ? void 0 : settings.loginUrl);
    localStorage.setItem(AUTH_LOGOUT_KEY, settings === null || settings === void 0 ? void 0 : settings.logoutUrl);
    localStorage.setItem(AUTH_RESET_PASSWORD_KEY, settings === null || settings === void 0 ? void 0 : settings.resetPasswordUrl);
    (settings === null || settings === void 0 ? void 0 : settings.resetPasswordErrorCode) && localStorage.setItem(RESET_PASSWORD_CODE_KEY, settings.resetPasswordErrorCode);
    localStorage.setItem(HEADER_USER_ID_KEY, JSON.stringify({ header: settings === null || settings === void 0 ? void 0 : settings.impersonatingUserIdHeaderName }));
}
